import { defineStore } from "pinia";
import { ref, computed, Ref, ComputedRef } from "vue";
import { ElMessage } from "element-plus";
import {
  fetchTags,
  createTag as apiCreateTag,
  updateTag as apiUpdateTag,
  deleteTag as apiDeleteTag,
} from "./tagApi";
import { eventBus } from "@shared/domain/events/EventBus";
import { DialogEvents } from "@shared/domain/dialog/DialogEvents";

// 标签类型定义
export interface Tag {
  id: string;
  name: string;
  category: TagCategory;
  color: string;
  description?: string;
  priority: TagPriority;
  usage_count: number;
  created_at: string;
  updated_at?: string;
}

export type TagCategory =
  | "疾病"
  | "人群"
  | "慢病管理"
  | "疗效"
  | "病例类型"
  | "中医证型"
  | "中医治法"
  | "经验沉淀"
  | "其他";

// 标签优先级类型
export type TagPriority = "low" | "normal" | "high";

// 标签表单类型
export interface TagForm {
  name: string;
  category: TagCategory | "";
  color: string;
  description: string;
  priority: TagPriority;
}

// 分类映射类型
export interface CategoryMap {
  [key: string]: string;
}

// 病历记录类型（简化版，用于标注）
export interface HealthRecordForAnnotation {
  id?: string;
  _id?: string;
  patient_name?: string;
  visit_date?: string;
  chief_complaint?: string;
  tags?: string[];
}

export const useTagStore = defineStore("tag", () => {
  // 薄DDD重构 - 使用事件总线替代直接依赖dialogStore
  // 这样domain层就不需要依赖外部层次

  // ==================== 状态管理 ====================

  // 对话框显示状态（通过事件总线控制）
  const showTagDialog: Ref<boolean> = ref(false);
  const showTagFormDialog: Ref<boolean> = ref(false);
  const showDeleteTagConfirm: Ref<boolean> = ref(false);
  const showAnnotateDialog: Ref<boolean> = ref(false);
  const showTagList: Ref<boolean> = ref(false);

  // 监听事件总线
  const setupEventListeners = () => {
    eventBus.on(DialogEvents.TAG_MANAGE_OPEN, () => {
      showTagDialog.value = true;
    });
    eventBus.on(DialogEvents.TAG_MANAGE_CLOSE, () => {
      showTagDialog.value = false;
    });

    eventBus.on(DialogEvents.TAG_FORM_OPEN, () => {
      showTagFormDialog.value = true;
    });
    eventBus.on(DialogEvents.TAG_FORM_CLOSE, () => {
      showTagFormDialog.value = false;
    });

    eventBus.on(DialogEvents.TAG_ANNOTATE_OPEN, () => {
      showAnnotateDialog.value = true;
    });
    eventBus.on(DialogEvents.TAG_ANNOTATE_CLOSE, () => {
      showAnnotateDialog.value = false;
    });
  };

  // 设置事件监听
  setupEventListeners();

  // 标签数据
  const allTags: Ref<Tag[]> = ref([]);
  const filteredTags: Ref<Tag[]> = ref([]);
  const editingTag: Ref<Tag | null> = ref(null);
  const tagToDelete: Ref<Tag | null> = ref(null);

  // ✅ 新增：标注相关状态
  const recentRecords: Ref<HealthRecordForAnnotation[]> = ref([]);
  const searchedRecords: Ref<HealthRecordForAnnotation[]> = ref([]);
  const selectedRecord: Ref<HealthRecordForAnnotation | null> = ref(null);
  const currentRecordTags: Ref<string[]> = ref([]);
  const availableTags: Ref<Tag[]> = ref([]);
  const annotationFilter: Ref<string> = ref("");
  const recordSelectionMode: Ref<"recent" | "search"> = ref("recent");
  const annotationLoading: Ref<boolean> = ref(false);

  // 加载状态
  const loading: Ref<boolean> = ref(false);

  // 搜索和筛选状态
  const tagSearchQuery: Ref<string> = ref("");
  const currentCategory: Ref<TagCategory | "all"> = ref("all");

  // 标签表单数据
  const tagForm: Ref<TagForm> = ref({
    name: "",
    category: "",
    color: "#1890ff",
    description: "",
    priority: "normal",
  });

  // ==================== 计算属性 ====================

  const isTagFormValid: ComputedRef<boolean> = computed(() => {
    return !!(
      tagForm.value.name.trim() &&
      tagForm.value.category &&
      tagForm.value.color
    );
  });

  const getMedicalTagsCount: ComputedRef<number> = computed(
    () =>
      allTags.value.filter((tag) => tag.category === ("疾病" as TagCategory))
        .length,
  );

  const getSymptomTagsCount: ComputedRef<number> = computed(
    () =>
      allTags.value.filter(
        (tag) => tag.category === ("中医证型" as TagCategory),
      ).length,
  );

  const getTreatmentTagsCount: ComputedRef<number> = computed(
    () =>
      allTags.value.filter(
        (tag) => tag.category === ("中医治法" as TagCategory),
      ).length,
  );

  // ==================== 对话框控制方法 ====================

  /**
   * 打开标签管理对话框
   * 薄DDD重构 - 使用事件总线
   */
  const openTagDialog = async (): Promise<void> => {
    eventBus.emit(DialogEvents.TAG_MANAGE_OPEN);
    await loadTagsFromAPI();
  };

  /**
   * 关闭标签管理对话框
   * 薄DDD重构 - 使用事件总线
   */
  const closeTagDialog = (): void => {
    eventBus.emit(DialogEvents.TAG_MANAGE_CLOSE);
    showTagFormDialog.value = false;
    showDeleteTagConfirm.value = false;
    resetTagForm();
  };

  /**
   * 显示创建标签表单
   * 薄DDD重构 - 使用事件总线
   */
  const showCreateTagForm = (): void => {
    editingTag.value = null;
    resetTagForm();
    eventBus.emit(DialogEvents.TAG_FORM_OPEN);
  };

  /**
   * 关闭标签表单对话框
   * 薄DDD重构 - 使用事件总线
   */
  const closeTagFormDialog = (): void => {
    eventBus.emit(DialogEvents.TAG_FORM_CLOSE);
    editingTag.value = null;
    resetTagForm();
  };

  // ==================== 数据加载方法 ====================

  /**
   * 从后端加载标签数据
   */
  const loadTagsFromAPI = async (): Promise<void> => {
    loading.value = true;

    try {
      const response = await fetchTags();

      // API返回的数据适配
      const tags = Array.isArray(response)
        ? response
        : (response as any).items || [];

      // 确保数据格式正确
      allTags.value = tags.map(
        (tag: Record<string, any>): Tag => ({
          id: tag._id || tag.id,
          name: tag.name,
          category: tag.category,
          color: tag.color,
          description: tag.description || "",
          priority: tag.priority || "normal",
          usage_count: tag.usage_count || 0,
          created_at: tag.created_at,
          updated_at: tag.updated_at,
        }),
      );

      filterTags();
    } catch (error: unknown) {
      console.error("TagStore - 加载标签失败:", error);
      ElMessage.error(
        "加载标签失败：" +
          ((error as { message?: string }).message || "网络错误"),
      );
      // 如果加载失败，使用示例数据作为后备
      initializeSampleTags();
    } finally {
      loading.value = false;
    }
  };

  /**
   * 初始化示例标签数据（作为后备）
   */
  const initializeSampleTags = (): void => {
    const sampleTags: Tag[] = [
      {
        id: "sample-1",
        name: "高血压",
        category: "疾病",
        color: "#f5222d",
        priority: "normal",
        usage_count: 15,
        created_at: "2024-01-01",
      },
      {
        id: "sample-2",
        name: "糖尿病",
        category: "疾病",
        color: "#f5222d",
        priority: "normal",
        usage_count: 12,
        created_at: "2024-01-02",
      },
      {
        id: "sample-3",
        name: "有效",
        category: "疗效",
        color: "#52c41a",
        priority: "normal",
        usage_count: 8,
        created_at: "2024-01-03",
      },
      {
        id: "sample-4",
        name: "教学案例",
        category: "病例类型",
        color: "#722ed1",
        priority: "normal",
        usage_count: 5,
        created_at: "2024-01-04",
      },
      {
        id: "sample-5",
        name: "经典医案",
        category: "经验沉淀",
        color: "#faad14",
        priority: "normal",
        usage_count: 3,
        created_at: "2024-01-05",
      },
    ];
    allTags.value = sampleTags;
    filteredTags.value = sampleTags;
  };

  // ==================== 标签操作方法 ====================

  /**
   * 筛选标签
   */
  const filterTags = (): void => {
    let filtered = allTags.value;

    // 按分类筛选
    if (currentCategory.value !== "all") {
      filtered = filtered.filter(
        (tag) => tag.category === currentCategory.value,
      );
    }

    // 按搜索关键词筛选
    if (tagSearchQuery.value.trim()) {
      const query = tagSearchQuery.value.toLowerCase();
      filtered = filtered.filter(
        (tag) =>
          tag.name.toLowerCase().includes(query) ||
          (tag.description && tag.description.toLowerCase().includes(query)),
      );
    }

    filteredTags.value = filtered;
  };

  /**
   * 编辑标签
   */
  const editTag = (tag: Tag): void => {
    editingTag.value = tag;
    Object.assign(tagForm.value, {
      name: tag.name,
      category: tag.category,
      color: tag.color,
      description: tag.description || "",
      priority: tag.priority || "normal",
    });
    showTagFormDialog.value = true;
  };

  /**
   * 复制标签
   */
  const duplicateTag = (tag: Tag): void => {
    editingTag.value = null;
    Object.assign(tagForm.value, {
      name: tag.name + " 复制",
      category: tag.category,
      color: tag.color,
      description: tag.description || "",
      priority: tag.priority || "normal",
    });
    showTagFormDialog.value = true;
  };

  /**
   * 删除标签
   */
  const deleteTag = (tag: Tag): void => {
    tagToDelete.value = tag;
    showDeleteTagConfirm.value = true;
  };

  /**
   * 确认删除标签
   */
  const confirmDeleteTag = async (): Promise<void> => {
    if (!tagToDelete.value) return;

    loading.value = true;

    try {
      await apiDeleteTag(tagToDelete.value.id);

      // 从本地列表中移除
      const index = allTags.value.findIndex(
        (tag) => tag.id === tagToDelete.value!.id,
      );
      if (index !== -1) {
        allTags.value.splice(index, 1);
        filterTags();
      }

      ElMessage.success(`删除标签"${tagToDelete.value.name}"成功！`);
    } catch (error: unknown) {
      console.error("删除标签失败:", error);
      ElMessage.error(
        `删除标签失败：${(error as { message?: string }).message || "网络错误"}`,
      );
    } finally {
      loading.value = false;
      showDeleteTagConfirm.value = false;
      tagToDelete.value = null;
    }
  };

  /**
   * 保存标签
   */
  const saveTag = async (): Promise<void> => {
    if (!isTagFormValid.value) {
      ElMessage.warning("请填写完整的标签信息");
      return;
    }

    loading.value = true;
    try {
      const tagData = {
        name: tagForm.value.name.trim(),
        category: tagForm.value.category as TagCategory,
        color: tagForm.value.color,
        description: tagForm.value.description.trim() || undefined,
        priority: tagForm.value.priority || "normal",
      };

      if (editingTag.value) {
        // 更新标签
        const response = await apiUpdateTag(editingTag.value.id, tagData);

        // 更新本地数据
        const index = allTags.value.findIndex(
          (tag) => tag.id === editingTag.value!.id,
        );
        if (index !== -1) {
          allTags.value[index] = {
            ...allTags.value[index],
            ...tagData,
            id:
              (response as any)._id ||
              (response as any).id ||
              editingTag.value.id,
            updated_at: new Date().toISOString(),
          };
        }

        ElMessage.success("更新标签成功！");
      } else {
        // 创建新标签
        const response = await apiCreateTag({
          name: tagData.name,
          category: tagData.category,
          color: tagData.color,
          description: tagData.description,
        });

        const newTagData: Tag = {
          id:
            (response as any)._id ||
            (response as any).id ||
            Date.now().toString(),
          ...tagData,
          usage_count: 0,
          created_at: new Date().toISOString(),
        };

        allTags.value.unshift(newTagData);
        ElMessage.success("创建标签成功！");
      }

      filterTags();
      closeTagFormDialog();
    } catch (error: unknown) {
      console.error("保存标签失败:", error);
      ElMessage.error(
        "保存标签失败：" +
          ((error as { message?: string }).message || "网络错误"),
      );
    } finally {
      loading.value = false;
    }
  };

  /**
   * 重置标签表单
   */
  const resetTagForm = (): void => {
    Object.assign(tagForm.value, {
      name: "",
      category: "",
      color: "#1890ff",
      description: "",
      priority: "normal",
    });
  };

  /**
   * 刷新标签列表
   */
  const refreshTagList = async (): Promise<void> => {
    await loadTagsFromAPI();
    ElMessage.success("刷新完成！");
  };

  /**
   * 设置搜索查询
   */
  const setSearchQuery = (query: string): void => {
    tagSearchQuery.value = query;
    filterTags();
  };

  /**
   * 设置当前分类
   */
  const setCurrentCategory = (category: TagCategory | "all"): void => {
    currentCategory.value = category;
    filterTags();
  };

  // ==================== 标注功能方法 ====================

  /**
   * 打开标注对话框
   * 薄DDD重构 - 使用事件总线
   */
  const openAnnotateDialog = (): void => {
    eventBus.emit(DialogEvents.TAG_ANNOTATE_OPEN);
    availableTags.value = [...allTags.value];
  };

  /**
   * 关闭标注对话框
   * 薄DDD重构 - 使用事件总线
   */
  const closeAnnotateDialog = (): void => {
    eventBus.emit(DialogEvents.TAG_ANNOTATE_CLOSE);
    selectedRecord.value = null;
    currentRecordTags.value = [];
    annotationFilter.value = "";
  };

  /**
   * 切换标签列表显示
   */
  const toggleTagList = (): void => {
    showTagList.value = !showTagList.value;
  };

  /**
   * 选中记录进行标注
   * @param record - 病历记录
   */
  const selectRecordForAnnotation = (
    record: HealthRecordForAnnotation,
  ): void => {
    selectedRecord.value = record;
    currentRecordTags.value = record.tags ? [...record.tags] : [];
  };

  /**
   * 添加标签到当前记录
   * @param tag - 标签对象或标签名
   */
  const addTagToRecord = (tag: Tag | string): void => {
    const tagName = typeof tag === "string" ? tag : tag.name;

    if (!currentRecordTags.value.includes(tagName)) {
      currentRecordTags.value.push(tagName);
    }
  };

  /**
   * 从当前记录移除标签
   * @param tagIndex - 标签索引
   */
  const removeTagFromRecord = (tagIndex: number): void => {
    if (tagIndex >= 0 && tagIndex < currentRecordTags.value.length) {
      currentRecordTags.value.splice(tagIndex, 1);
    }
  };

  /**
   * 保存标注
   * @param updateCallback - 更新回调函数（由组件提供）
   */
  const saveAnnotation = async (
    updateCallback?: (recordId: string, tags: string[]) => Promise<void>,
  ): Promise<boolean> => {
    if (!selectedRecord.value) {
      ElMessage.warning("请选择要标注的记录");
      return false;
    }

    annotationLoading.value = true;

    try {
      const recordId = selectedRecord.value.id || selectedRecord.value._id;
      if (!recordId) {
        throw new Error("记录ID无效");
      }

      // 如果提供了回调函数，使用回调函数更新
      if (updateCallback) {
        await updateCallback(recordId, currentRecordTags.value);
      }

      // 更新本地数据
      if (selectedRecord.value) {
        selectedRecord.value.tags = [...currentRecordTags.value];
      }

      ElMessage.success("标注保存成功");

      // 清空选择
      selectedRecord.value = null;
      currentRecordTags.value = [];

      return true;
    } catch (error: unknown) {
      console.error("保存标注失败:", error);
      ElMessage.error(
        "保存标注失败：" +
          ((error as { message?: string }).message || "网络错误"),
      );
      return false;
    } finally {
      annotationLoading.value = false;
    }
  };

  /**
   * 设置最近记录列表
   * @param records - 记录列表
   */
  const setRecentRecords = (records: HealthRecordForAnnotation[]): void => {
    recentRecords.value = records;
  };

  /**
   * 设置搜索记录列表
   * @param records - 记录列表
   */
  const setSearchedRecords = (records: HealthRecordForAnnotation[]): void => {
    searchedRecords.value = records;
  };

  /**
   * 设置记录选择模式
   * @param mode - 选择模式
   */
  const setRecordSelectionMode = (mode: "recent" | "search"): void => {
    recordSelectionMode.value = mode;
  };

  /**
   * 设置标注过滤器
   * @param filter - 过滤文本
   */
  const setAnnotationFilter = (filter: string): void => {
    annotationFilter.value = filter;
  };

  /**
   * 清空标注选择
   */
  const clearAnnotationSelection = (): void => {
    selectedRecord.value = null;
    currentRecordTags.value = [];
  };

  // ==================== 工具方法 ====================

  /**
   * 获取分类名称
   */
  const getCategoryName = (category: TagCategory): string => {
    return category || "未知";
  };

  /**
   * 获取对比色
   */
  const getContrastColor = (hexColor: string): string => {
    // 将十六进制颜色转换为RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    // 计算亮度
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // 根据亮度返回黑色或白色
    return brightness > 128 ? "#000000" : "#ffffff";
  };

  // ==================== 返回状态和方法 ====================

  return {
    // 状态
    showTagDialog,
    showTagFormDialog,
    showDeleteTagConfirm,
    showAnnotateDialog, // ✅ 新增
    showTagList, // ✅ 新增
    allTags,
    filteredTags,
    editingTag,
    tagToDelete,
    loading,
    tagSearchQuery,
    currentCategory,
    tagForm,

    // ✅ 新增：标注相关状态
    recentRecords,
    searchedRecords,
    selectedRecord,
    currentRecordTags,
    availableTags,
    annotationFilter,
    recordSelectionMode,
    annotationLoading,

    // 计算属性
    isTagFormValid,
    getMedicalTagsCount,
    getSymptomTagsCount,
    getTreatmentTagsCount,

    // 对话框控制方法
    openTagDialog,
    closeTagDialog,
    showCreateTagForm,
    closeTagFormDialog,

    // 数据加载方法
    loadTagsFromAPI,
    refreshTagList,

    // 标签操作方法
    filterTags,
    editTag,
    duplicateTag,
    deleteTag,
    confirmDeleteTag,
    saveTag,
    resetTagForm,
    setSearchQuery,
    setCurrentCategory,

    // ✅ 新增：标注功能方法
    openAnnotateDialog,
    closeAnnotateDialog,
    toggleTagList,
    selectRecordForAnnotation,
    addTagToRecord,
    removeTagFromRecord,
    saveAnnotation,
    setRecentRecords,
    setSearchedRecords,
    setRecordSelectionMode,
    setAnnotationFilter,
    clearAnnotationSelection,

    // 工具方法
    getCategoryName,
    getContrastColor,
  };
});
